/**
 * Main-thread handle on the routing Web Worker. Loads Fanar's road graph once
 * (5.7 MB gzipped, cached by the service worker as an immutable asset) and
 * answers route requests from it. No key, no quota, works offline.
 */
import type { Route } from "../../shared/route.js";
import type { RouteOpts } from "../router/route.js";

export const GRAPH_URL = "/graph/albania-v1.bin.gz";

type Result =
  | { kind: "ok"; routes: Route[] }
  | { kind: "no_route" }
  | { kind: "off_network" }
  | { kind: "unavailable"; reason: string };

export class LocalRouter {
  private worker: Worker | null = null;
  private seq = 0;
  private pending = new Map<number, (msg: { ok: boolean; result?: unknown; error?: string; ms?: number }) => void>();
  private loaded: Promise<boolean> | null = null;
  lastMs = 0;

  private ensureWorker(): Worker {
    if (this.worker !== null) return this.worker;
    const w = new Worker(new URL("../router/router.worker.ts", import.meta.url), { type: "module" });
    w.onmessage = (ev: MessageEvent<{ id: number; ok: boolean; result?: unknown; error?: string; ms?: number }>) => {
      const cb = this.pending.get(ev.data.id);
      if (cb !== undefined) { this.pending.delete(ev.data.id); cb(ev.data); }
    };
    w.onerror = () => { for (const cb of this.pending.values()) cb({ ok: false, error: "worker" }); this.pending.clear(); };
    this.worker = w;
    return w;
  }

  private call(msg: Record<string, unknown>): Promise<{ ok: boolean; result?: unknown; error?: string; ms?: number }> {
    const id = ++this.seq;
    return new Promise((resolve) => {
      this.pending.set(id, resolve);
      this.ensureWorker().postMessage({ id, ...msg });
    });
  }

  /** Start the download early (at "Nis udhëtimin"), so the first search is instant. */
  preload(): Promise<boolean> {
    this.loaded ??= this.call({ type: "load", url: GRAPH_URL }).then((r) => {
      if (!r.ok) this.loaded = null; // retry on the next call
      return r.ok;
    });
    return this.loaded;
  }

  async route(from: { lat: number; lon: number }, to: { lat: number; lon: number }, opts: RouteOpts): Promise<Result> {
    const ready = await this.preload();
    if (!ready) return { kind: "unavailable", reason: "graph" };
    const r = await this.call({ type: "route", from: { lat: from.lat, lon: from.lon }, to: { lat: to.lat, lon: to.lon }, opts });
    if (!r.ok) return { kind: "unavailable", reason: r.error ?? "worker" };
    this.lastMs = r.ms ?? 0;
    const result = r.result as Route[] | { error: "no_route" | "off_network" };
    if ("error" in result) return { kind: result.error };
    return { kind: "ok", routes: result };
  }
}
